/**
 * §F5.10's two selects, and the poll shape behind the four job formats.
 *
 * A module of its own rather than constants inside `ExportModal.tsx`, for the reason
 * `viewportBbox.ts` gives: a component file that also exports values defeats fast refresh,
 * and these are the pieces a test wants to reason about without rendering anything.
 */
import type { ExportExtent, ExportFormat } from '../../lib/api';

/** §F5.10's Format select, in its own order. `csv` never becomes a job (§E8). */
export const FORMATS: ExportFormat[] = ['png', 'pdf', 'tif', 'gpkg', 'csv'];

/** §F5.10's Extent select, in its own order. */
export const EXTENTS: ExportExtent[] = ['view', 'programme', 'unit', 'aoi'];

/**
 * The two extents CSV can actually honour.
 *
 * `/indicators/export` is a **row** query: it takes `level`, `year`, `country`, `parent` and
 * `unit_id`, and it has nowhere to put a bounding box or a drawn polygon. "Current view" and
 * "Drawn area (AOI)" therefore mean nothing to it — and left selectable they were worse than
 * useless, because the request still succeeded and quietly returned the whole level. The
 * panel disables them for `csv`, with the reason in the option's tooltip, exactly as it does
 * for an AOI that has not been drawn.
 */
export const CSV_EXTENTS: ExportExtent[] = ['programme', 'unit'];

/**
 * The formats that hand over the data rather than a picture of it —
 * `apps.analysis.export.render.DOWNLOADABLE` on the server.
 *
 * Mirrored here only to say, before the submit, that this format leaves out the layers the
 * reader's role may look at but not download (`export.downloadNote`). *Which* layers, and
 * how many, is the server's `withheld` echo — the panel does not hold the catalogue. The
 * strip itself is the server's and is never the client's to decide, which is §I2's rule that
 * the UI mirror is never the enforcement.
 */
export const DOWNLOADABLE: ExportFormat[] = ['tif', 'gpkg'];

/** §F6's poll shape, ported here so Task 9 does not depend on Task 10's hook. */
export const POLL_START_MS = 1200;
export const POLL_FACTOR = 1.5;
export const POLL_MAX_MS = 5000;

/** How long a run may take before the panel stops asking. §F6's own cap. */
export const POLL_TIMEOUT_MS = 120_000;

/** The next poll delay: 1.2 s, ×1.5 each time, never above 5 s. */
export const nextDelay = (delay: number): number =>
  Math.min(POLL_MAX_MS, delay * POLL_FACTOR);
